import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, GraduationCap, Sparkles, Landmark, ArrowRight, Target, Eye,
  Mail, MapPin, Phone, Building2, Award, Users, ChevronDown, Linkedin,
} from 'lucide-react';
import PageHero from '../../components/public/PageHero';
import Reveal from '../../components/public/Reveal';
import api from '../../lib/api';

const VALUE_ICONS = { ShieldCheck, GraduationCap, Sparkles, Landmark, Target, Eye, Award, Users };

export default function Nosotros() {
  const [about, setAbout] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get('/about').then(({ data }) => setAbout(data)).catch(() => {});
  }, []);

  if (!about) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-slate-500" data-testid="nosotros-loading">
        Cargando información institucional…
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="nosotros-page">
      <PageHero
        eyebrow={about.hero?.eyebrow || 'Nuestra institución'}
        title={about.hero?.title || 'Cámara Nacional Inmobiliaria de Bolivia'}
        subtitle={about.hero?.subtitle || ''}
        cover_image={about.hero?.cover_image}
      />

      {/* Misión / Visión */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="nosotros-mision-vision">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
          {about.mission?.body && (
            <Reveal>
              <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cnib-blue/10">
                  <Target className="h-6 w-6 text-cnib-blue" />
                </span>
                <h2 className="mt-5 font-heading text-3xl font-extrabold text-cnib-navy">{about.mission.title || 'Misión'}</h2>
                <p className="mt-4 whitespace-pre-wrap leading-relaxed text-slate-600">{about.mission.body}</p>
              </div>
            </Reveal>
          )}
          {about.vision?.body && (
            <Reveal delay={0.1}>
              <div className="h-full rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white p-8 shadow-sm">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500/10">
                  <Eye className="h-6 w-6 text-orange-500" />
                </span>
                <h2 className="mt-5 font-heading text-3xl font-extrabold text-cnib-navy">{about.vision.title || 'Visión'}</h2>
                <p className="mt-4 whitespace-pre-wrap leading-relaxed text-slate-600">{about.vision.body}</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Valores */}
      {Array.isArray(about.values) && about.values.length > 0 && (
        <section className="py-16 md:py-24" data-testid="nosotros-valores">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-orange-500">Lo que nos mueve</p>
              <h2 className="mt-3 text-center font-heading text-4xl font-extrabold text-cnib-navy">Pilares institucionales</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {about.values.map((v, i) => {
                const Icon = VALUE_ICONS[v.icon] || Sparkles;
                return (
                  <Reveal key={i} delay={0.05 * i}>
                    <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-xl">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-orange-500/10">
                        <Icon className="h-5 w-5 text-orange-500" />
                      </span>
                      <h3 className="mt-4 font-heading text-lg font-bold text-cnib-navy">{v.title}</h3>
                      {v.text && <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>}
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Historia */}
      {about.history?.body && (
        <section className="bg-cnib-navy py-16 text-white md:py-24" data-testid="nosotros-historia">
          <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 md:grid-cols-5 md:items-center lg:px-8">
            <Reveal className="md:col-span-3">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-orange-400">Trayectoria</p>
              <h2 className="mt-3 font-heading text-4xl font-extrabold">{about.history.title || 'Nuestra historia'}</h2>
              <p className="mt-6 whitespace-pre-wrap leading-relaxed text-slate-300">{about.history.body}</p>
            </Reveal>
            {about.history.image && (
              <Reveal delay={0.15} className="md:col-span-2">
                <img src={about.history.image} alt="Historia CNIB" className="rounded-3xl shadow-2xl" />
              </Reveal>
            )}
          </div>
        </section>
      )}

      {/* Directorio */}
      {Array.isArray(about.directory) && about.directory.length > 0 && (
        <MemberGrid title="Consejo Directivo" eyebrow="Liderazgo" items={about.directory} testid="nosotros-directorio" />
      )}

      {/* Equipo */}
      {Array.isArray(about.team) && about.team.length > 0 && (
        <section className="bg-slate-50 py-16 md:py-24">
          <MemberGrid title="Equipo operativo" eyebrow="Nuestro equipo" items={about.team} testid="nosotros-equipo" small />
        </section>
      )}

      {/* Regionales */}
      {Array.isArray(about.regionals) && about.regionals.length > 0 && (
        <section className="py-16 md:py-24" data-testid="nosotros-regionales">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-orange-500">Presencia nacional</p>
              <h2 className="mt-3 text-center font-heading text-4xl font-extrabold text-cnib-navy">Regionales</h2>
            </Reveal>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {about.regionals.map((r, i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <article className="h-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl" data-testid={`regional-card-${i}`}>
                    {r.photo ? (
                      <img src={r.photo} alt={r.name} className="h-40 w-full object-cover" />
                    ) : (
                      <div className="flex h-40 items-center justify-center bg-gradient-to-br from-cnib-blue to-cnib-navy">
                        <Building2 className="h-14 w-14 text-white/50" />
                      </div>
                    )}
                    <div className="space-y-2.5 p-6">
                      <h3 className="font-heading text-xl font-extrabold text-cnib-navy">{r.name}</h3>
                      {r.president && (
                        <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Presidente: {r.president}</p>
                      )}
                      {r.city && (
                        <p className="flex items-start gap-2 text-sm text-slate-600">
                          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                          <span>{r.address ? `${r.address}, ${r.city}` : r.city}</span>
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2 pt-1">
                        {r.phone && (
                          <a href={`tel:${r.phone}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200">
                            <Phone className="h-3 w-3" /> {r.phone}
                          </a>
                        )}
                        {r.email && (
                          <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-200">
                            <Mail className="h-3 w-3" /> {r.email}
                          </a>
                        )}
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Certificaciones */}
      {Array.isArray(about.certifications) && about.certifications.length > 0 && (
        <section className="bg-slate-50 py-16 md:py-24" data-testid="nosotros-certificaciones">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-orange-500">Respaldos</p>
              <h2 className="mt-3 text-center font-heading text-4xl font-extrabold text-cnib-navy">Certificaciones y acreditaciones</h2>
            </Reveal>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {about.certifications.map((c, i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <div className="group flex h-full flex-col items-center rounded-2xl border border-slate-200 bg-white p-6 text-center transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`cert-card-${i}`}>
                    {c.logo_url ? (
                      <img src={c.logo_url} alt={c.name} className="h-20 w-auto object-contain transition-transform group-hover:scale-105" />
                    ) : (
                      <Award className="h-16 w-16 text-slate-300" />
                    )}
                    <h3 className="mt-4 font-heading text-base font-extrabold text-cnib-navy">{c.name}</h3>
                    {c.description && <p className="mt-2 text-xs text-slate-500">{c.description}</p>}
                    {c.link && (
                      <a href={c.link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-cnib-blue hover:text-orange-500">
                        Más info <ArrowRight className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Alianzas */}
      {Array.isArray(about.alliances) && about.alliances.length > 0 && (
        <section className="py-14" data-testid="nosotros-alianzas">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-slate-400">Alianzas estratégicas</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
              {about.alliances.map((a, i) => {
                const content = a.logo_url ? (
                  <img src={a.logo_url} alt={a.name} className="h-12 w-auto grayscale opacity-70 transition-all hover:grayscale-0 hover:opacity-100" />
                ) : (
                  <span className="font-heading text-lg font-bold text-slate-500">{a.name}</span>
                );
                return a.link ? (
                  <a key={i} href={a.link} target="_blank" rel="noopener noreferrer">{content}</a>
                ) : (
                  <div key={i}>{content}</div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {Array.isArray(about.faqs) && about.faqs.length > 0 && (
        <section className="bg-slate-50 py-16 md:py-24" data-testid="nosotros-faqs">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-orange-500">Preguntas frecuentes</p>
              <h2 className="mt-3 text-center font-heading text-4xl font-extrabold text-cnib-navy">¿Qué más necesitas saber?</h2>
            </Reveal>
            <div className="mt-10 space-y-3">
              {about.faqs.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={i} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`faq-card-${i}`}>
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span className="font-heading text-base font-bold text-cnib-navy">{f.question}</span>
                      <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180 text-orange-500' : ''}`} />
                    </button>
                    {open && (
                      <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 text-sm leading-relaxed text-slate-600 whitespace-pre-wrap">
                        {f.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA final */}
      <section className="relative overflow-hidden bg-cnib-navy py-16 text-center">
        <div className="relative mx-auto max-w-3xl px-4">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-orange-400">Formá parte</p>
          <h2 className="mt-3 font-heading text-3xl font-extrabold text-white md:text-4xl">
            Acompañamos y promovemos el crecimiento del sector inmobiliario
          </h2>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/hagase-socio" className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 text-sm font-bold text-white hover:bg-orange-600" data-testid="nosotros-cta-afiliate">
              Afíliate ahora <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contacto" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white hover:border-orange-500 hover:text-orange-400" data-testid="nosotros-cta-contacto">
              Contáctanos
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function MemberGrid({ title, eyebrow, items, testid, small = false }) {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-testid={testid}>
      <Reveal>
        <p className="text-center text-xs font-bold uppercase tracking-[0.3em] text-orange-500">{eyebrow}</p>
        <h2 className="mt-3 text-center font-heading text-4xl font-extrabold text-cnib-navy">{title}</h2>
      </Reveal>
      <div className={`mt-12 grid gap-6 ${small ? 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
        {items.map((m, i) => (
          <Reveal key={i} delay={0.04 * i}>
            <article className="group h-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl" data-testid={`${testid}-card-${i}`}>
              <div className={`relative overflow-hidden ${small ? 'aspect-square' : 'aspect-[4/5]'} bg-slate-100`}>
                {m.photo ? (
                  <img src={m.photo} alt={m.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-cnib-blue to-cnib-navy">
                    <Users className="h-16 w-16 text-white/40" />
                  </div>
                )}
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-extrabold text-cnib-navy">{m.name}</h3>
                {m.role && <p className="text-xs font-bold uppercase tracking-wider text-orange-500">{m.role}</p>}
                {m.bio && <p className="mt-2 line-clamp-3 text-sm text-slate-600">{m.bio}</p>}
                <div className="mt-3 flex items-center gap-2">
                  {m.email && (
                    <a href={`mailto:${m.email}`} className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-cnib-blue hover:text-white">
                      <Mail className="h-3.5 w-3.5" />
                    </a>
                  )}
                  {m.linkedin && (
                    <a href={m.linkedin} target="_blank" rel="noopener noreferrer" className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-cnib-blue hover:text-white">
                      <Linkedin className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
